import { Portal, Transition } from "@headlessui/react";
import {
  createContext,
  Fragment,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useReducer,
  type Dispatch,
  type ReactNode,
} from "react";
import {
  getModalPresentation,
  modalStackReducer,
  type ModalStackAction,
  type ModalStackBehavior,
  type ModalStackEntry,
} from "./modalStackUtils";

export type { ModalStackBehavior } from "./modalStackUtils";

interface ModalStackContextState {
  entries: ModalStackEntry[];
  dispatch: Dispatch<ModalStackAction>;
}

const ModalStackContext = createContext<ModalStackContextState | null>(null);

export interface ModalStackProviderProps {
  children: ReactNode;
}

function ModalStackBackdrop({
  show,
  position,
}: Readonly<{ show: boolean; position: number }>) {
  return (
    <Portal>
      <Transition
        appear
        show={show}
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          aria-hidden
          className="fixed inset-0 bg-black/75"
          style={{ zIndex: 20 + position * 2 }}
          data-modal-backdrop
        />
      </Transition>
    </Portal>
  );
}

export function ModalStackProvider({
  children,
}: Readonly<ModalStackProviderProps>) {
  const [entries, dispatch] = useReducer(modalStackReducer, []);
  const context = useMemo(() => ({ entries, dispatch }), [entries]);

  return (
    <ModalStackContext.Provider value={context}>
      {children}
      <ModalStackBackdrop
        show={entries.some((entry) => entry.status === "open")}
        position={Math.max(entries.length - 1, 0)}
      />
    </ModalStackContext.Provider>
  );
}

export function useModalStack(show: boolean, behavior: ModalStackBehavior) {
  const context = useContext(ModalStackContext);
  const dispatch = context?.dispatch;
  const id = useId();

  useLayoutEffect(() => {
    if (!dispatch) return;

    if (show) {
      dispatch({ type: "activate", id, behavior });
    } else {
      dispatch({ type: "beginClose", id });
    }
  }, [behavior, dispatch, id, show]);

  useLayoutEffect(
    () => () => {
      dispatch?.({ type: "deactivate", id, force: true });
    },
    [dispatch, id],
  );

  const presentation = context && getModalPresentation(context.entries, id);

  return {
    position: presentation?.layer ?? 0,
    isEnabled: !!context,
    isRegistered: !!presentation?.registered,
    isTopmost: !!presentation?.topmost,
    isSuspended: !!presentation?.registered && !presentation.visible,
    completeClose: () => {
      dispatch?.({ type: "deactivate", id });
    },
  };
}
